import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { API_BASE_URL } from '../services/api';
import { useSEO } from '../hooks/useSEO';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const formGlow = keyframes`
  0% {
    box-shadow:
      0 24px 48px rgba(82, 63, 44, 0.08),
      0 0 0 0 rgba(222, 179, 135, 0.2);
  }
  50% {
    box-shadow:
      0 26px 60px rgba(82, 63, 44, 0.12),
      0 0 0 6px rgba(222, 179, 135, 0.28);
  }
  100% {
    box-shadow:
      0 24px 48px rgba(82, 63, 44, 0.08),
      0 0 0 0 rgba(222, 179, 135, 0.18);
  }
`;

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Lillaflora",
  "description": "Florist och inredningsbutik i Kramfors med gåvor, ljus & doftljus och personliga råd.",
  "url": "https://lillaflora.nu",
  "telephone": "+46-123-456-78",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Kramfors",
    "addressRegion": "Västernorrland",
    "addressCountry": "SE"
  },
  "openingHours": "Mo-Fr 10:00-18:00, Sa 10:00-15:00",
  "priceRange": "$$",
  "image": "https://lillaflora.nu/static/images/lillaflora-hero.jpg",
  "logo": "https://lillaflora.nu/favicon.ico"
};

const PageWrapper = styled.div`
  background: linear-gradient(180deg, #fefcf8 0%, #f8efe6 46%, #faf3ec 100%);
  overflow-x: hidden;
`;

const HeroBackdrop = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(110px, 18vw, 180px) 0 clamp(96px, 12vw, 150px);
  background:
    radial-gradient(circle at 12% 18%, rgba(108, 129, 117, 0.08), transparent 58%),
    radial-gradient(circle at 82% 12%, rgba(215, 186, 155, 0.17), transparent 58%),
    radial-gradient(circle at 18% 88%, rgba(171, 146, 128, 0.12), transparent 54%),
    linear-gradient(165deg, #fefbf7 0%, #f8eee4 45%, #fbf4ed 100%);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(182deg, rgba(255, 254, 252, 0.5) 0%, rgba(255, 255, 255, 0) 22%),
      radial-gradient(circle at 6% 93%, rgba(208, 184, 161, 0.08) 0%, transparent 52%),
      radial-gradient(circle at 94% 4%, rgba(168, 180, 168, 0.05) 0%, transparent 48%);
  }

  @media (max-width: 768px) {
    padding: 96px 0 80px;
  }
`;

const HeroInner = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: clamp(56px, 7vw, 108px);
  max-width: clamp(1000px, 78vw, 1120px);
  margin: 0 auto;
  width: 100%;
  padding: 0 clamp(26px, 4.5vw, 56px);

  @media (min-width: 1400px) {
    max-width: clamp(1080px, 72vw, 1180px);
    gap: clamp(72px, 7.5vw, 110px);
  }

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: center;
    gap: clamp(40px, 8vw, 58px);
  }
`;

const HeroContent = styled.div`
  display: grid;
  gap: clamp(22px, 3.6vw, 32px);
  max-width: clamp(440px, 42vw, 500px);
  text-align: left;
  z-index: 2;
  animation: ${fadeUp} 520ms ease-out 90ms both;

  @media (min-width: 1101px) {
    justify-items: start;
    padding-right: clamp(32px, 3.8vw, 46px);
  }

  @media (max-width: 768px) {
    text-align: center;
    justify-items: center;
    width: 100%;
    padding: 0 clamp(12px, 6vw, 22px);
  }

  @media (max-width: 1100px) {
    max-width: 540px;
  }
`;

const HeroBadge = styled.span`
  width: fit-content;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(70, 83, 75, 0.12);
  color: rgba(45, 56, 50, 0.82);
  font-weight: 600;
  font-size: clamp(0.82rem, 0.9vw, 0.92rem);
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.32rem, 3vw, 2.94rem);
  font-weight: 630;
  color: #2b2725;
  line-height: clamp(1.1, 1.35vw, 1.16);
  letter-spacing: -0.008em;
  max-width: 22ch;
`;

const HeroLead = styled.span`
  font-size: clamp(1rem, 2.2vw, 1.22rem);
  font-weight: 600;
  color: rgba(43, 41, 39, 0.78);
  font-style: italic;
  max-width: 30ch;

  @media (max-width: 768px) {
    max-width: 26ch;
  }
`;

const HeroText = styled.p`
  font-size: clamp(1.05rem, 2.1vw, 1.24rem);
  line-height: 1.92;
  color: rgba(43, 41, 39, 0.72);
  max-width: 520px;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const HeroHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: clamp(18px, 3.2vw, 24px);
  justify-items: stretch;
  width: 100%;
  animation: ${fadeUp} 520ms ease-out 150ms both;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: clamp(14px, 5vw, 18px);
  }
`;

const HighlightCard = styled.div`
  padding: clamp(16px, 3vw, 22px);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(222, 210, 198, 0.34);
  box-shadow: 0 12px 28px rgba(40, 35, 30, 0.07);
  display: grid;
  gap: 6px;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const HighlightTitle = styled.span`
  font-weight: 600;
  color: #2e3933;
  font-size: 1rem;
`;

const HighlightDetail = styled.span`
  color: rgba(46, 57, 51, 0.7);
  font-size: 0.94rem;
  line-height: 1.6;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(14px, 3vw, 18px);
  align-items: center;
  animation: ${fadeUp} 520ms ease-out 180ms both;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const HeroActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: clamp(14px, 2.9vw, 18px) clamp(26px, 4.4vw, 36px);
  border-radius: 999px;
  background: linear-gradient(145deg, #2f3a34 0%, #4a5a52 100%);
  color: #f8f3ed;
  font-weight: 600;
  text-decoration: none;
  font-size: clamp(1rem, 2vw, 1.06rem);
  box-shadow: 0 18px 34px rgba(22, 28, 24, 0.28);
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
  border: none;
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 26px 44px rgba(22, 28, 24, 0.32);
    background: linear-gradient(145deg, #36453f 0%, #53635b 100%);
  }
`;

const HeroActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: clamp(14px, 2.8vw, 18px) clamp(22px, 3.8vw, 32px);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #2f3a34;
  font-weight: 600;
  text-decoration: none;
  font-size: clamp(1rem, 1.9vw, 1.04rem);
  border: 1px solid rgba(222, 210, 198, 0.58);
  box-shadow: 0 16px 36px rgba(22, 18, 14, 0.12);
  transition: transform 0.22s ease, box-shadow 0.22s ease, color 0.22s ease, background 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 22px 44px rgba(22, 18, 14, 0.18);
    background: rgba(248, 243, 237, 0.94);
    color: #2b2725;
  }
`;

const HeroAside = styled.div`
  display: grid;
  gap: clamp(22px, 3vw, 32px);
  width: min(100%, clamp(380px, 36vw, 420px));
  animation: ${fadeUp} 560ms ease-out 210ms both;

  @media (max-width: 1100px) {
    width: min(100%, 520px);
  }
`;

const HeroCard = styled.div`
  position: relative;
  display: grid;
  gap: clamp(14px, 2.4vw, 18px);
  padding: clamp(28px, 3.6vw, 36px);
  border-radius: clamp(30px, 4.2vw, 40px);
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(224, 206, 188, 0.56);
  box-shadow: 0 32px 52px -28px rgba(42, 34, 26, 0.22);
`;

const HeroCardHeading = styled.span`
  font-size: clamp(1.12rem, 2vw, 1.24rem);
  font-weight: 620;
  color: #2b2725;
`;

const HeroCardDescription = styled.p`
  font-size: clamp(0.96rem, 1.8vw, 1.04rem);
  line-height: 1.7;
  color: rgba(43, 41, 39, 0.72);
  margin: 0;
`;

const HeroContactRow = styled.div`
  display: grid;
  gap: 12px;
`;

const HeroContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 18px;
  background: rgba(248, 243, 237, 0.86);
  color: #2f3a34;
  text-decoration: none;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.2s ease;
  width: fit-content;

  &:hover {
    background: rgba(236, 225, 212, 0.94);
    transform: translateY(-2px);
  }
`;

const ContactCallout = styled.section`
  position: relative;
  z-index: 5;
  padding: clamp(52px, 8vw, 88px) 0 clamp(60px, 9vw, 110px);
`;

const CalloutInner = styled.div`
  margin: 0 auto;
  width: min(960px, calc(100% - clamp(34px, 9vw, 120px)));
  background: linear-gradient(135deg, rgba(252, 245, 236, 0.96) 0%, rgba(248, 229, 210, 0.88) 38%, rgba(249, 238, 229, 0.92) 100%);
  border-radius: clamp(32px, 4vw, 44px);
  border: 1px solid rgba(226, 202, 176, 0.6);
  box-shadow: 0 40px 80px -32px rgba(80, 58, 32, 0.18);
  padding: clamp(36px, 5vw, 48px) clamp(32px, 5.4vw, 60px);
  display: grid;
  gap: clamp(18px, 3vw, 26px);
  text-align: center;
`;

const CalloutTitle = styled.h2`
  font-size: clamp(1.76rem, 2.4vw, 2.2rem);
  font-weight: 640;
  color: #d86b52;
  margin: 0;
`;

const CalloutText = styled.p`
  font-size: clamp(1.02rem, 2vw, 1.16rem);
  line-height: 1.8;
  color: #8a5b3a;
  margin: 0 auto;
  max-width: 620px;
`;

const CalloutActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(14px, 3vw, 18px);
`;

const CalloutAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: clamp(14px, 2.8vw, 18px) clamp(24px, 3.8vw, 32px);
  border-radius: 999px;
  font-weight: 600;
  font-size: clamp(1rem, 1.9vw, 1.04rem);
  text-decoration: none;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, color 0.22s ease;
`;

const CalloutPrimary = styled(CalloutAction)`
  background: linear-gradient(145deg, #e88963 0%, #d86b52 100%);
  color: #fff6ef;
  box-shadow: 0 18px 34px rgba(199, 99, 63, 0.26);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 24px 44px rgba(199, 99, 63, 0.32);
  }
`;

const CalloutSecondary = styled(CalloutAction)`
  background: rgba(255, 255, 255, 0.8);
  color: #c96448;
  border: 1px solid rgba(232, 182, 152, 0.6);
  box-shadow: 0 16px 32px rgba(120, 80, 42, 0.12);

  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 252, 249, 0.92);
  }
`;

const ContactSection = styled.section`
  position: relative;
  padding: 0 0 clamp(90px, 13vw, 150px);
`;

const ContactInner = styled.div`
  margin: 0 auto;
  width: min(1180px, calc(100% - clamp(32px, 8vw, 96px)));
  display: grid;
  grid-template-columns: 1.12fr 0.88fr;
  gap: clamp(36px, 5vw, 52px);

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const FormColumn = styled.div`
  position: relative;
`;

const ContactForm = styled.form<{ highlighted?: boolean }>`
  position: relative;
  display: grid;
  gap: clamp(18px, 3vw, 26px);
  padding: clamp(32px, 4.2vw, 44px);
  border-radius: clamp(28px, 4vw, 36px);
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(220, 204, 184, 0.58);
  box-shadow:
    0 28px 56px -26px rgba(48, 35, 21, 0.26),
    0 24px 48px rgba(82, 63, 44, 0.08);
  transition: box-shadow 0.4s ease;

  ${({ highlighted }) =>
    highlighted &&
    css`
      animation: ${formGlow} 2.4s ease-in-out;
      box-shadow:
        0 26px 60px -24px rgba(150, 112, 65, 0.25),
        0 0 0 4px rgba(228, 186, 142, 0.35);
      background: rgba(255, 252, 247, 0.98);
    `}
`;

const FormHeader = styled.div`
  display: grid;
  gap: 10px;
`;

const FormTitle = styled.h3`
  font-size: clamp(1.42rem, 2.2vw, 1.72rem);
  font-weight: 620;
  color: #2f332f;
  margin: 0;
`;

const FormSubtitle = styled.p`
  font-size: clamp(0.98rem, 1.8vw, 1.06rem);
  line-height: 1.7;
  color: rgba(43, 41, 39, 0.68);
  margin: 0;
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: 14px 18px;
  border-radius: 18px;
  font-weight: 560;
  font-size: 0.98rem;
  border: 1px solid transparent;

  ${({ type }) =>
    type === 'success'
      ? css`
          background: rgba(198, 222, 208, 0.38);
          color: #255f3c;
          border-color: rgba(130, 171, 147, 0.6);
        `
      : css`
          background: rgba(244, 216, 216, 0.4);
          color: #8c2f2f;
          border-color: rgba(229, 174, 174, 0.6);
        `}
`;

const FormGroup = styled.div`
  display: grid;
  gap: 10px;

  label {
    font-weight: 560;
    color: rgba(43, 41, 39, 0.78);
    font-size: 0.96rem;
  }

  input,
  textarea {
    width: 100%;
    padding: 14px 18px;
    border-radius: 16px;
    border: 1px solid rgba(206, 192, 176, 0.68);
    background: rgba(255, 255, 255, 0.9);
    font-size: 1rem;
    transition: border 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

    &:focus {
      outline: none;
      border-color: rgba(210, 172, 132, 0.9);
      box-shadow: 0 0 0 3px rgba(228, 191, 151, 0.25);
      background: rgba(255, 253, 249, 0.94);
    }
  }

  textarea {
    min-height: 140px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  border-radius: 999px;
  background: linear-gradient(145deg, #2f3a34 0%, #4a5a52 100%);
  color: #f8f3ed;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  box-shadow: 0 18px 34px rgba(22, 28, 24, 0.24);
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 26px 44px rgba(22, 28, 24, 0.3);
    background: linear-gradient(145deg, #36453f 0%, #53635b 100%);
  }

  &:disabled {
    background: rgba(88, 98, 93, 0.68);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const InfoColumn = styled.div`
  display: grid;
  gap: clamp(22px, 3.4vw, 32px);
`;

const InfoGrid = styled.div`
  display: grid;
  gap: clamp(20px, 3vw, 26px);
`;

const InfoPanel = styled.div`
  padding: clamp(22px, 3.2vw, 30px);
  border-radius: clamp(24px, 3.6vw, 32px);
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(220, 204, 184, 0.52);
  box-shadow: 0 22px 48px -26px rgba(48, 32, 18, 0.2);
  display: grid;
  gap: 14px;
`;

const InfoPanelTitle = styled.h4`
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: clamp(1.08rem, 1.9vw, 1.2rem);
  font-weight: 620;
  color: #2f332f;
  margin: 0;
`;

const InfoPanelText = styled.p`
  margin: 0;
  font-size: clamp(0.98rem, 1.7vw, 1.06rem);
  line-height: 1.7;
  color: rgba(43, 41, 39, 0.72);
`;

const InfoPanelLink = styled.a`
  color: #2f3a34;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    height: 1px;
    background: rgba(47, 58, 52, 0.4);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.2s ease;
  }

  &:hover:after {
    transform: scaleX(1);
  }
`;

const MapPanel = styled(InfoPanel)`
  padding: clamp(22px, 3vw, 28px);
  gap: clamp(16px, 2.6vw, 22px);
`;

const MapWrapper = styled.div`
  border-radius: clamp(18px, 3vw, 22px);
  overflow: hidden;
  box-shadow: 0 18px 32px -18px rgba(40, 32, 24, 0.16);
  border: 1px solid rgba(220, 204, 184, 0.4);
  height: clamp(220px, 28vw, 280px);
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 16px;
  background: rgba(248, 243, 237, 0.82);
  color: #2f3a34;
  text-decoration: none;
  font-weight: 580;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(236, 225, 212, 0.92);
    transform: translateY(-2px);
  }
`;

const Contact: React.FC = () => {
  useSEO({
    entityType: 'page',
    entityId: 'contact',
    fallbackTitle: 'Kontakt – Lillaflora',
    fallbackDescription:
      'Kontakta vår florist i Kramfors för presenttips, beställning, leveransvillkor och personlig service.',
    fallbackImage: '/static/images/lillaflora-hero.jpg'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  
  const [formHighlighted, setFormHighlighted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const scrollToForm = () => {
    if (formRef.current) {
      // Scroll to form with smooth animation
      formRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Highlight the form
      setFormHighlighted(true);
      
      // Remove highlight after animation
      setTimeout(() => {
        setFormHighlighted(false);
      }, 2500);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear any previous status message when user starts typing
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const baseUrl = (process.env.REACT_APP_API_URL || API_BASE_URL || '').replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: data.message || 'Tack för ditt meddelande! Vi hör av oss så snart vi kan.'
        });
        setFormData({ name: '', email: '', subject: '', message: '' });

        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'ads_conversion_Contact_Us_1', {});
        }
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || 'Det uppstod ett fel. Vänligen försök igen.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Det uppstod ett nätverksfel. Kontrollera din internetanslutning och försök igen.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>
      <ContactCallout>
        <CalloutInner>
          <CalloutTitle>🌹 Skicka blombud med Lillaflora 🌹</CalloutTitle>
          <CalloutText>
            Vill du sprida värme till någon du tycker om? Våra floristbundna buketter levereras med omsorg.
            Ring oss direkt eller fyll i formuläret så återkopplar vi med förslag som passar just din hälsning.
          </CalloutText>
          <CalloutActions>
            <CalloutPrimary href="tel:0708555052">📞 Ring 070&nbsp;855&nbsp;50&nbsp;52</CalloutPrimary>
            <CalloutSecondary as="button" type="button" onClick={scrollToForm}>
              📝 Fyll i önskemål
            </CalloutSecondary>
          </CalloutActions>
        </CalloutInner>
      </ContactCallout>

      <HeroBackdrop>
        <HeroInner>
          <HeroContent>
            <HeroBadge>Din blomsterbutik i Kramfors</HeroBadge>
            <HeroTitle>Kontakta oss</HeroTitle>
            <HeroLead>Vi finns här för att hålla mysfaktorn levande – i butiken, online och i dina blomsterhälsningar.</HeroLead>
            <HeroText>
              Har du frågor om sortimentet, vill boka ett blombud eller behöver hjälp med ett personligt arrangemang?
              Hör av dig så guidar vi dig med värme och omtanke.
            </HeroText>
            <HeroHighlights>
              <HighlightCard>
                <HighlightTitle>Personlig service</HighlightTitle>
                <HighlightDetail>Vi återkopplar alltid inom 24 timmar på vardagar och hjälper dig hela vägen.</HighlightDetail>
              </HighlightCard>
              <HighlightCard>
                <HighlightTitle>Butik i hjärtat av Kramfors</HighlightTitle>
                <HighlightDetail>Torggatan 1 – välkommen in för att botanisera bland blommor, presenter och inredning.</HighlightDetail>
              </HighlightCard>
              <HighlightCard>
                <HighlightTitle>Blombud & arrangemang</HighlightTitle>
                <HighlightDetail>Vi skapar buketter för alla tillfällen och levererar med samma omtanke som du känner.</HighlightDetail>
              </HighlightCard>
            </HeroHighlights>
            <HeroActions>
              <HeroActionButton type="button" onClick={scrollToForm}>
                📝 Skicka en förfrågan
              </HeroActionButton>
              <HeroActionLink href="tel:0708555052">
                📞 Ring oss direkt
              </HeroActionLink>
            </HeroActions>
          </HeroContent>

          <HeroAside>
            <HeroCard>
              <HeroCardHeading>Vi hjälper dig gärna personligen</HeroCardHeading>
              <HeroCardDescription>
                Oavsett om du vill beställa blommor till någon du håller av, planerar en högtid eller behöver stylingtips
                för hemmet så hittar vi en mysig lösning tillsammans.
              </HeroCardDescription>
              <HeroContactRow>
                <HeroContactLink href="tel:0708555052">📞 070&nbsp;855&nbsp;50&nbsp;52</HeroContactLink>
                <HeroContactLink href="mailto:butik@lillaflora.nu">✉️ butik@lillaflora.nu</HeroContactLink>
              </HeroContactRow>
            </HeroCard>
            <HeroCard>
              <HeroCardHeading>Välkommen till butiken</HeroCardHeading>
              <HeroCardDescription>
                Torggatan 1, 872&nbsp;30 Kramfors<br />
                Vi håller dörren öppen med doften av nybundna buketter och noga utvalt sortiment.
              </HeroCardDescription>
              <HeroContactRow>
                <HeroContactLink href="#vägbeskrivning">📍 Se vägbeskrivning</HeroContactLink>
              </HeroContactRow>
            </HeroCard>
          </HeroAside>
        </HeroInner>
      </HeroBackdrop>

      <ContactSection>
        <ContactInner>
          <FormColumn>
            <ContactForm ref={formRef} onSubmit={handleSubmit} highlighted={formHighlighted}>
              <FormHeader>
                <FormTitle>Skicka ett meddelande</FormTitle>
                <FormSubtitle>
                  Skriv några rader om vad du behöver hjälp med. Vi svarar så snart vi kan, oftast inom ett dygn.
                </FormSubtitle>
              </FormHeader>

              {submitStatus.type && (
                <StatusMessage type={submitStatus.type}>{submitStatus.message}</StatusMessage>
              )}

              <FormGroup>
                <label htmlFor="name">Namn</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="email">E-post</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="subject">Ämne</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="message">Meddelande</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Berätta gärna vad vi kan hjälpa dig med..."
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Skickar...' : 'Skicka meddelande'}
              </SubmitButton>
            </ContactForm>
          </FormColumn>

          <InfoColumn>
            <InfoGrid>
              <InfoPanel>
                <InfoPanelTitle>🏪 Butiksinformation</InfoPanelTitle>
                <InfoPanelText>
                  Lillaflora är din mysiga blomsterbutik i hjärtat av Kramfors. Vi handplockar blommor, gåvor och
                  inredning som gör hemmet ombonat och varmt.
                </InfoPanelText>
              </InfoPanel>

              <InfoPanel>
                <InfoPanelTitle>📧 E-post &amp; telefon</InfoPanelTitle>
                <InfoPanelText>
                  Har du en fråga eller vill du boka ett blombud? Hör av dig via telefon eller mail så återkommer vi så
                  snart vi kan.
                </InfoPanelText>
                <InfoPanelLink href="mailto:butik@lillaflora.nu">✉️ butik@lillaflora.nu</InfoPanelLink>
                <InfoPanelLink href="tel:0708555052">📞 070&nbsp;855&nbsp;50&nbsp;52</InfoPanelLink>
                <InfoPanelText>Vi svarar vanligtvis inom 24 timmar på vardagar.</InfoPanelText>
              </InfoPanel>
            </InfoGrid>

            <MapPanel id="vägbeskrivning">
              <InfoPanelTitle>📍 Besöksadress</InfoPanelTitle>
              <InfoPanelText>Torggatan 1, 872&nbsp;30 Kramfors</InfoPanelText>
              <MapWrapper>
                <MapFrame
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1749.8657437165836!2d17.775887516086043!3d62.93418068196847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4665b6c6b9b8c4d9%3A0x4d8b8c0e8d9e8c8b!2sTorggatan%201%2C%20872%2030%20Kramfors%2C%20Sweden!5e0!3m2!1sen!2sse!4v1693839472820!5m2!1sen!2sse"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lillaflora butik i Kramfors"
                />
              </MapWrapper>
            </MapPanel>

            <InfoPanel>
              <InfoPanelTitle>📱 Följ vår vardag</InfoPanelTitle>
              <InfoPanelText>Ta del av mysiga nyheter, bukett-inspiration och glimtar från butiken.</InfoPanelText>
              <SocialLinks>
                <SocialLink
                  href="https://www.facebook.com/p/Lilla-flora-i-Kramfors-61564983360569/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📘 Facebook
                </SocialLink>
                <SocialLink
                  href="https://www.instagram.com/lillaflorakramfors/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📷 Instagram
                </SocialLink>
              </SocialLinks>
            </InfoPanel>
          </InfoColumn>
        </ContactInner>
      </ContactSection>
    </PageWrapper>
  );
};

export default Contact;
